import { useCampaignStats } from '@round/creatorbase-helpers/api-hooks';
import { getTableMeta, UnallocatedCampaignsRow } from '../../helpers';
import { CellContext } from '@tanstack/react-table';
import { Skeleton, ProgressBar } from '@round/ui-kit';
import styles from './ProgressCell.module.css';
import { asMoneyInKNotation, asMoneyWithoutZeros, getExponent } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { toDecimalPoint } from '@round/utils';
import { Tooltip } from '@round/whitelabel-ui';

type Context = CellContext<UnallocatedCampaignsRow, any>;

const ProgressCell = ({ row: { original }, table }: Context) => {
    const { getCurrencyById } = useCurrencies();
    const { isLoading } = getTableMeta(table);

    const { data: campaignStats, isLoading: isCampaignStatsLoading } = useCampaignStats(
        original.id ? { campaign_id: original.id } : null,
        { enabled: !!original.id }
    );

    if (isLoading || isCampaignStatsLoading || Object.keys(original).length === 0) {
        return (
            <div className={styles.container}>
                <Skeleton width="100%" />
                <Skeleton width="100%" />
            </div>
        );
    }

    const totalBudgetCurrencyValuePair = Object.entries(campaignStats?.total_budget || {})[0] || [];
    const totalBudget = totalBudgetCurrencyValuePair.length ? Number(totalBudgetCurrencyValuePair[1]) : null;

    const spentPercentage = campaignStats?.progress_percentage ?? null;
    const spentPercentageLabel = typeof spentPercentage === 'number' ? `${toDecimalPoint(spentPercentage, 2)}%` : '-';

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                <Tooltip.Root>
                    {(anchor, setAnchor) => (
                        <>
                            <p ref={setAnchor}>
                                {asMoneyInKNotation({
                                    amount: totalBudget,
                                    currency: getCurrencyById(original.currency_id),
                                    threshold: 5,
                                })}
                            </p>
                            <Tooltip anchorElement={anchor} disabled={!totalBudget || getExponent(totalBudget) <= 5}>
                                <Tooltip.Body>
                                    <span>
                                        {asMoneyWithoutZeros(totalBudget, getCurrencyById(original.currency_id))}
                                    </span>
                                </Tooltip.Body>
                            </Tooltip>
                        </>
                    )}
                </Tooltip.Root>
                <p className={styles.percentAllocated}>{spentPercentageLabel}</p>
            </div>
            <ProgressBar progress={spentPercentage ?? 0} barClassName={styles.bar} trackClassName={styles.track} />
        </div>
    );
};

export default ProgressCell;
